import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';

// ---------------------------------------------------------------------------
// Safe Markdown rendering (builds React nodes — never dangerouslySetInnerHTML,
// so any data echoed back in an answer becomes text, not markup). Images are
// restricted to an allow-listed host in the renderer as well as the data layer.
// ---------------------------------------------------------------------------
function isAllowedImg(url) {
  try {
    const u = new URL(url);
    return u.protocol === 'https:' && u.hostname.toLowerCase().endsWith('.blob.core.windows.net');
  } catch {
    return false;
  }
}

function AsstImage({ url, caption }) {
  const [open, setOpen] = useState(false);
  if (!isAllowedImg(url)) return <>{caption || ''}</>;
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        title={caption || 'View'}
        className="block my-1"
      >
        <img src={url} alt={caption || 'image'} loading="lazy" className="max-h-28 rounded border border-gray-200" />
      </button>
      {open && (
        <div
          onClick={() => setOpen(false)}
          className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/80 p-4"
        >
          <img
            src={url}
            alt={caption || 'image'}
            onClick={(e) => e.stopPropagation()}
            className="max-h-[85vh] max-w-[90vw] rounded"
          />
        </div>
      )}
    </>
  );
}

// Inline: image (before link), bold, italic, code, link.
const INLINE =
  /(!\[([^\]]*)\]\((https?:\/\/[^)\s]+)\)|\*\*([^*]+)\*\*|\*([^*]+)\*|`([^`]+)`|\[([^\]]+)\]\((https?:\/\/[^)\s]+)\))/g;

function renderInline(text, key) {
  const out = [];
  let last = 0;
  let m;
  let i = 0;
  INLINE.lastIndex = 0;
  while ((m = INLINE.exec(text)) !== null) {
    if (m.index > last) out.push(text.slice(last, m.index));
    const k = `${key}-${i++}`;
    if (m[3] !== undefined) out.push(<AsstImage key={k} url={m[3]} caption={m[2] || null} />);
    else if (m[4] !== undefined) out.push(<strong key={k}>{m[4]}</strong>);
    else if (m[5] !== undefined) out.push(<em key={k}>{m[5]}</em>);
    else if (m[6] !== undefined) out.push(<code key={k} className="px-1 py-0.5 rounded bg-gray-100 text-[0.85em]">{m[6]}</code>);
    else if (m[7] && m[8])
      out.push(
        <a key={k} href={m[8]} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">
          {m[7]}
        </a>
      );
    last = m.index + m[0].length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

function Markdown({ content }) {
  const lines = content.replace(/\r\n/g, '\n').split('\n');
  const blocks = [];
  let list = null;
  let para = [];
  const flushPara = () => {
    if (para.length) {
      const items = para;
      blocks.push(
        <p key={blocks.length} className="mb-2 last:mb-0">
          {items.map((l, j) => (
            <span key={j}>
              {j > 0 && <br />}
              {renderInline(l, `p${blocks.length}-${j}`)}
            </span>
          ))}
        </p>
      );
      para = [];
    }
  };
  const flushList = () => {
    if (list) {
      const items = list;
      blocks.push(
        <ul key={blocks.length} className="list-disc pl-5 mb-2 space-y-1">
          {items.map((it, j) => (
            <li key={j}>{renderInline(it, `u${blocks.length}-${j}`)}</li>
          ))}
        </ul>
      );
      list = null;
    }
  };
  for (const raw of lines) {
    const line = raw.trimEnd();
    const bullet = /^\s*[-*+]\s+(.*)$/.exec(line);
    const numbered = /^\s*\d+\.\s+(.*)$/.exec(line);
    if (line.trim() === '') {
      flushPara();
      flushList();
    } else if (bullet) {
      flushPara();
      (list = list || []).push(bullet[1]);
    } else if (numbered) {
      flushPara();
      (list = list || []).push(numbered[1]);
    } else {
      flushList();
      para.push(line);
    }
  }
  flushPara();
  flushList();
  return <div className="text-sm leading-relaxed text-gray-800">{blocks}</div>;
}

// ---------------------------------------------------------------------------
// Page context → sent as a hint so "this page" style questions resolve.
// ---------------------------------------------------------------------------
const SUGGESTIONS = [
  'How many stations are there, broken down by owner?',
  'Which stations are the most frequently damaged?',
  "What's the wayleave progress breakdown?",
  'Give me a quick overview of the data.',
];

export default function DataAssistant() {
  const location = useLocation();
  const [enabled, setEnabled] = useState(false);
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [toolHint, setToolHint] = useState('');
  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  const abortRef = useRef(null);

  // Check whether the assistant is configured on the server.
  useEffect(() => {
    let active = true;
    fetch('/api/assistant/config', { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : { enabled: false }))
      .then((d) => {
        if (active) setEnabled(!!d.enabled);
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, []);

  // Auto-scroll on new content; focus input when opened.
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, toolHint]);
  useEffect(() => {
    if (open && inputRef.current) inputRef.current.focus();
  }, [open]);

  const ask = useCallback(
    async (question) => {
      const q = (question || '').trim();
      if (!q || loading) return;
      const history = messages.map((m) => ({ role: m.role, content: m.content }));
      const assistantId = `a-${Date.now()}`;
      setMessages((prev) => [
        ...prev,
        { id: `u-${Date.now()}`, role: 'user', content: q },
        { id: assistantId, role: 'assistant', content: '' },
      ]);
      setInput('');
      setLoading(true);
      setToolHint('');

      const append = (delta) =>
        setMessages((prev) => prev.map((m) => (m.id === assistantId ? { ...m, content: m.content + delta } : m)));

      const controller = new AbortController();
      abortRef.current = controller;

      try {
        const res = await fetch('/api/assistant/ask', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          signal: controller.signal,
          body: JSON.stringify({ question: q, history, context: { page: location.pathname } }),
        });
        if (!res.ok || !res.body) {
          append(res.status === 503 ? '⚠️ The assistant is not configured yet.' : `⚠️ Request failed (${res.status}).`);
          return;
        }
        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        for (;;) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          let idx;
          while ((idx = buffer.indexOf('\n\n')) >= 0) {
            const line = buffer.slice(0, idx).trim();
            buffer = buffer.slice(idx + 2);
            if (!line.startsWith('data:')) continue;
            let evt;
            try {
              evt = JSON.parse(line.slice(5).trim());
            } catch {
              continue;
            }
            if (evt.type === 'text') {
              setToolHint('');
              append(evt.value || '');
            } else if (evt.type === 'tool') {
              setToolHint('Looking through the data…');
            } else if (evt.type === 'error') {
              setToolHint('');
              append(`⚠️ ${evt.message || 'Error'}`);
            } else if (evt.type === 'done') {
              setToolHint('');
            }
          }
        }
      } catch (err) {
        if (err.name !== 'AbortError') append('⚠️ Connection error.');
      } finally {
        setLoading(false);
        setToolHint('');
        abortRef.current = null;
      }
    },
    [loading, messages, location.pathname]
  );

  const stop = () => {
    if (abortRef.current) abortRef.current.abort();
  };

  if (!enabled) return null;

  return (
    <>
      {/* Floating launcher */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed bottom-5 right-5 z-[9998] flex items-center gap-2 rounded-full bg-primary-600 hover:bg-primary-700 text-white shadow-lg px-4 py-3 transition-all duration-200"
          title="Ask the Data Assistant"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 3v-3z" />
          </svg>
          <span className="text-sm font-medium hidden sm:inline">Data Assistant</span>
        </button>
      )}

      {/* Chat panel */}
      {open && (
        <div className="fixed inset-0 sm:inset-auto sm:bottom-5 sm:right-5 z-[9998] flex flex-col bg-white sm:rounded-xl shadow-2xl border border-gray-200 sm:w-[400px] sm:h-[600px] sm:max-h-[85vh]">
          {/* Header */}
          <header className="flex items-center gap-2 px-4 py-3 border-b border-gray-200 bg-gray-50 sm:rounded-t-xl">
            <svg className="w-5 h-5 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 3v-3z" />
            </svg>
            <span className="font-semibold text-gray-900">Data Assistant</span>
            <span
              aria-label="Beta feature"
              className="text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded-full bg-indigo-50 text-indigo-700"
            >
              Beta
            </span>
            <div className="ml-auto flex items-center gap-1">
              {messages.length > 0 && (
                <button
                  onClick={() => setMessages([])}
                  className="p-1.5 rounded-md text-gray-500 hover:bg-gray-200"
                  title="Clear conversation"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              )}
              <button
                onClick={() => setOpen(false)}
                className="p-1.5 rounded-md text-gray-500 hover:bg-gray-200"
                title="Close"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </header>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3" aria-live="polite">
            {messages.length === 0 && (
              <div className="text-sm text-gray-600">
                <p className="mb-3">Ask me about stations, damage records, wayleaves and field visits. For example:</p>
                <div className="space-y-2">
                  {SUGGESTIONS.map((s) => (
                    <button
                      key={s}
                      onClick={() => ask(s)}
                      className="block w-full text-left px-3 py-2 rounded-lg border border-gray-200 hover:border-primary-400 hover:bg-primary-50 text-gray-700 transition-colors"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {messages.map((m) =>
              m.role === 'user' ? (
                <div key={m.id} className="flex justify-end">
                  <div className="max-w-[85%] rounded-2xl rounded-br-sm bg-primary-600 text-white px-3 py-2 text-sm">
                    {m.content}
                  </div>
                </div>
              ) : (
                <div key={m.id} className="flex justify-start">
                  <div className="max-w-[90%] rounded-2xl rounded-bl-sm bg-gray-100 px-3 py-2">
                    {m.content ? <Markdown content={m.content} /> : <TypingDots />}
                  </div>
                </div>
              )
            )}

            {toolHint && (
              <div className="flex items-center gap-2 text-xs text-gray-500 pl-1">
                <span className="inline-block w-2 h-2 rounded-full bg-primary-500 animate-pulse" />
                {toolHint}
              </div>
            )}
          </div>

          {/* Input */}
          <form
            onSubmit={(e) => {
              e.preventDefault();
              ask(input);
            }}
            className="border-t border-gray-200 p-2"
          >
            <div className="flex items-end gap-2">
              <textarea
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    ask(input);
                  }
                }}
                rows={1}
                placeholder="Ask about the data…"
                disabled={loading}
                className="flex-1 resize-none max-h-28 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:bg-gray-50"
              />
              {loading ? (
                <button
                  type="button"
                  onClick={stop}
                  className="p-2 rounded-lg bg-gray-200 hover:bg-gray-300 text-gray-700"
                  title="Stop"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <rect x="6" y="6" width="12" height="12" rx="2" />
                  </svg>
                </button>
              ) : (
                <button
                  type="submit"
                  disabled={!input.trim()}
                  className="p-2 rounded-lg bg-primary-600 hover:bg-primary-700 disabled:opacity-40 text-white"
                  title="Send"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19V5m0 0l-7 7m7-7l7 7" />
                  </svg>
                </button>
              )}
            </div>
            {/* Persistent disclaimer — always visible, never scrolls away. */}
            <p className="text-[11px] text-gray-400 text-center mt-1.5">
              AI can make mistakes. Double-check important info.
            </p>
          </form>
        </div>
      )}
    </>
  );
}

function TypingDots() {
  return (
    <div className="flex items-center gap-1 py-1">
      <span className="w-1.5 h-1.5 rounded-full bg-gray-400 animate-bounce [animation-delay:-0.3s]" />
      <span className="w-1.5 h-1.5 rounded-full bg-gray-400 animate-bounce [animation-delay:-0.15s]" />
      <span className="w-1.5 h-1.5 rounded-full bg-gray-400 animate-bounce" />
    </div>
  );
}
